import cN from 'classnames';
import type * as React from 'react';
import Link from '../Link';
import styles from './style.m.less';

type CardProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & CardStyleProps;

type CardStyleProps = {
    isCompact?: boolean;
    /** Removes the transparency/blur from the card background. */
    isOpaque?: boolean;
};

/**
 *  The Card itself
 */
export function Card({ className, isOpaque = false, isCompact = false, ...props }: CardProps) {
    return (
        <article
            className={cN('card', className, {
                'card--opaque': isOpaque,
                [styles.compact]: isCompact,
            })}
            {...props}
            data-testid="card"
        />
    );
}

type LinkCardProps = React.ComponentPropsWithoutRef<typeof Link> & CardStyleProps;

/**
 * An interactive card with a link (via href prop).
 */
export function LinkCard({ className, isOpaque = false, isCompact = false, ...props }: LinkCardProps) {
    return (
        <Link
            className={cN('card', styles.link, className, {
                'card--opaque': isOpaque,
                [styles.compact]: isCompact,
            })}
            {...props}
        />
    );
}

/**
 *  The Card Header inside a card
 *  A CardHeader has CardHeaderHeading and CardHeaderButtons inside it
 */
export function CardHeader({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>) {
    return <header className={cN('card-header', className)} {...props} />;
}

export function CardHeaderHeading({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) {
    return <h1 className={cN('card-header-heading', className)} {...props} />;
}

export function CardHeaderButtons({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
    return <div className={cN('card-header-buttons', className)} {...props} />;
}

/**
 *  A Section inside a card
 *  A CardSection has CardSectionHeading and CardBody inside it
 */
export function CardSection({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>) {
    return <section className={cN('card-section', className)} {...props} />;
}

export function CardSectionHeading({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) {
    return <h1 className={cN('card-section-heading', className)} {...props} />;
}

export function CardSectionBody({
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
    return <div {...props} />;
}

type ThumbCardProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> &
    CardStyleProps & {
        isEnabled?: boolean;
    };

/**
 *  A card thumbnail, that represents a (often clickable) “preview” of a card,
 *  e.g. on an overview page
 */
export function ThumbCard({
    className,
    isEnabled = true,
    isOpaque = false,
    isCompact = false,
    ...props
}: ThumbCardProps) {
    return (
        <article
            className={cN(
                'card',
                styles.interactive,
                isEnabled ? styles.interactiveEnabled : styles.interactiveDisabled,
                {
                    'card--opaque': isOpaque,
                    [styles.compact]: isCompact,
                },
                className
            )}
            {...props}
        />
    );
}
